import styledSystemPropTypes from '@styled-system/prop-types';
import type * as CSS from 'csstype';
import type { InterpolationFunction } from 'styled-components';
import * as styledSystem from 'styled-system';
import type { LiteralUnion } from 'type-fest';

import type { Theme } from './theme';

/* Use styled-system's "compose" to create groups of styles so that we can
easily limit which system props to expose on a per-component basis.
e.g.
  styled(Base)`
    ${COMMON}
  `;
exposes the color (color, bg) and spacing (m, p, mt, pt, etc) props.
*/

const { compose } = styledSystem;

type CustomInterpolationFunction<T> = InterpolationFunction<T> & { propTypes?: any };

export type CommonProps = styledSystem.ColorProps<
  Theme,
  LiteralUnion<styledSystem.ThemeValue<'colors', Theme>, string>
> &
  styledSystem.SpaceProps<Theme, LiteralUnion<styledSystem.ThemeValue<'space', Theme>, string | number>> &
  styledSystem.BackgroundProps<Theme>;
export const COMMON: CustomInterpolationFunction<CommonProps> = compose(
  styledSystem.color,
  styledSystem.space,
  styledSystem.background,
);
COMMON.propTypes = {
  ...styledSystemPropTypes.color,
  ...styledSystemPropTypes.space,
  ...styledSystemPropTypes.background,
};

export type BorderProps = Omit<
  styledSystem.BordersProps<Theme>,
  'borderColor' | 'borderTopColor' | 'borderBottomColor' | 'borderLeftColor' | 'borderRightColor'
> &
  styledSystem.BorderColorProps<Theme, LiteralUnion<styledSystem.ThemeValue<'colors', Theme>, string>> &
  styledSystem.ShadowProps<Theme>;
export const BORDER: CustomInterpolationFunction<BorderProps> = compose(styledSystem.borders, styledSystem.shadow);
BORDER.propTypes = {
  ...styledSystemPropTypes.borders,
  ...styledSystemPropTypes.shadow,
};

export type TypographyProps = Omit<styledSystem.TypographyProps<Theme>, 'fontSize'> &
  styledSystem.FontSizeProps<Theme, LiteralUnion<styledSystem.ThemeValue<'fontSizes', Theme>, string | number>>;
export const TYPOGRAPHY: CustomInterpolationFunction<TypographyProps> = styledSystem.typography;
TYPOGRAPHY.propTypes = styledSystemPropTypes.typography;

export type LayoutProps = styledSystem.LayoutProps<Theme>;
export const LAYOUT: CustomInterpolationFunction<LayoutProps> = styledSystem.layout;
LAYOUT.propTypes = styledSystemPropTypes.layout;

export type PositionProps = Omit<styledSystem.PositionProps, 'zIndex'> & {
  zIndex?: styledSystem.ResponsiveValue<
    CSS.ZIndexProperty | styledSystem.ThemeValue<'zIndices', Theme> | number,
    Theme
  >;
};
export const POSITION: CustomInterpolationFunction<PositionProps> = styledSystem.position;
POSITION.propTypes = styledSystemPropTypes.position;

export type FlexboxProps = styledSystem.FlexboxProps<Theme>;
export const FLEXBOX: CustomInterpolationFunction<FlexboxProps> = styledSystem.flexbox;
FLEXBOX.propTypes = styledSystemPropTypes.flexbox;
